import { useEffect, useRef, useState } from 'react';
import type { SmartSnapMatchEvent, ViewportInfo } from '../../hooks/useFabricCanvas';
import { edgeToEdgeDistance } from '../../lib/smart-snap';

const RULER_THICKNESS = 22;

/** How long the indicator lingers after the last snap match
 *  before fading out (ms). Long enough to be visible during a
 *  steady drag, short enough to disappear once the user moves
 *  past the snap zone. */
const FLASH_LINGER_MS = 300;

interface SmartGuidesProps {
  viewport: ViewportInfo;
  rulersVisible: boolean;
  subscribeToSmartSnapMatch?: (cb: (m: SmartSnapMatchEvent) => void) => () => void;
}

/** Visual indicator for the smart-guide snap path. Subscribes to
 *  match events from the canvas hook and renders magenta lines
 *  spanning between the dragged layer's matched edge and the
 *  target layer's matched edge, plus a small label showing the
 *  gap distance.
 *
 *  Pointer events disabled — never intercepts the in-flight drag.
 *  Auto-clears after FLASH_LINGER_MS of no new matches; a sustained
 *  drag along a snap line keeps the indicator continuously lit
 *  because each tick refreshes the timeout. */
export function SmartGuides({
  viewport,
  rulersVisible,
  subscribeToSmartSnapMatch,
}: SmartGuidesProps) {
  const [active, setActive] = useState<SmartSnapMatchEvent | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!subscribeToSmartSnapMatch) return;
    return subscribeToSmartSnapMatch((m) => {
      setActive(m);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setActive(null);
        timerRef.current = null;
      }, FLASH_LINGER_MS);
    });
  }, [subscribeToSmartSnapMatch]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const offset = rulersVisible ? RULER_THICKNESS : 0;
  const w = Math.max(0, viewport.viewportWidth - offset);
  const h = Math.max(0, viewport.viewportHeight - offset);
  const z = viewport.zoom;
  if (!active || z <= 0) {
    return (
      <svg
        width={w}
        height={h}
        className="absolute z-[16] pointer-events-none"
        style={{ left: offset, top: offset, width: w, height: h }}
        aria-hidden="true"
      />
    );
  }

  /** Project a scene-space X coord to SVG-local pixel coords
   *  (relative to the overlay's top-left, which itself sits at
   *  `offset` inside the wrapper). */
  const sx = (sceneX: number) => sceneX * z + viewport.panX - offset;
  const sy = (sceneY: number) => sceneY * z + viewport.panY - offset;

  return (
    <svg
      width={w}
      height={h}
      className="absolute z-[16] pointer-events-none"
      style={{ left: offset, top: offset, width: w, height: h }}
      aria-hidden="true"
    >
      {active.matchX && renderMatch(active, active.matchX, 'x', sx, sy)}
      {active.matchY && renderMatch(active, active.matchY, 'y', sx, sy)}
    </svg>
  );
}

/** Render one axis's snap indicator: a magenta line spanning
 *  between the dragged bbox edge and the target bbox edge plus a
 *  distance label at the midpoint. */
function renderMatch(
  evt: SmartSnapMatchEvent,
  match: NonNullable<SmartSnapMatchEvent['matchX']>,
  axis: 'x' | 'y',
  sx: (n: number) => number,
  sy: (n: number) => number,
) {
  const target = evt.targets.find((t) => t.id === match.targetId);
  if (!target) return null;
  const src = evt.source;

  // For X-axis matches, the line is VERTICAL — runs from the
  // dragged bbox's Y range to the target bbox's Y range at
  // x=match.position (the snapped X coord). Symmetric for Y.
  if (axis === 'x') {
    const x = sx(match.position);
    // Span the indicator from the topmost of the two boxes to
    // the bottommost — gives a visual anchor showing both layers
    // share this X coordinate.
    const top = Math.min(src.top, target.top);
    const bottom = Math.max(src.top + src.height, target.top + target.height);
    const y1 = sy(top);
    const y2 = sy(bottom);
    const labelY = (y1 + y2) / 2;
    const dist = edgeToEdgeDistance('y', src, target);
    return (
      <g key={`x-${match.targetId}`}>
        <line x1={x + 0.5} y1={y1} x2={x + 0.5} y2={y2} stroke="#ec4899" strokeWidth={1} />
        {/* Small tick marks at each box's edge for emphasis. */}
        <Tick x={x} y={sy(src.top)} axis="x" />
        <Tick x={x} y={sy(src.top + src.height)} axis="x" />
        <Tick x={x} y={sy(target.top)} axis="x" />
        <Tick x={x} y={sy(target.top + target.height)} axis="x" />
        {dist > 0 && <DistanceLabel x={x + 6} y={labelY} text={`${Math.round(dist)} px`} />}
      </g>
    );
  }

  const y = sy(match.position);
  const left = Math.min(src.left, target.left);
  const right = Math.max(src.left + src.width, target.left + target.width);
  const x1 = sx(left);
  const x2 = sx(right);
  const labelX = (x1 + x2) / 2;
  const dist = edgeToEdgeDistance('x', src, target);
  return (
    <g key={`y-${match.targetId}`}>
      <line x1={x1} y1={y + 0.5} x2={x2} y2={y + 0.5} stroke="#ec4899" strokeWidth={1} />
      <Tick x={sx(src.left)} y={y} axis="y" />
      <Tick x={sx(src.left + src.width)} y={y} axis="y" />
      <Tick x={sx(target.left)} y={y} axis="y" />
      <Tick x={sx(target.left + target.width)} y={y} axis="y" />
      {dist > 0 && <DistanceLabel x={labelX} y={y - 6} text={`${Math.round(dist)} px`} />}
    </g>
  );
}

function Tick({ x, y, axis }: { x: number; y: number; axis: 'x' | 'y' }) {
  if (axis === 'x') {
    return <line x1={x - 3} y1={y} x2={x + 3} y2={y} stroke="#ec4899" strokeWidth={1} />;
  }
  return <line x1={x} y1={y - 3} x2={x} y2={y + 3} stroke="#ec4899" strokeWidth={1} />;
}

function DistanceLabel({ x, y, text }: { x: number; y: number; text: string }) {
  const padding = 4;
  const width = text.length * 6.5 + padding * 2;
  const height = 16;
  return (
    <g>
      <rect
        x={x - width / 2}
        y={y - height / 2}
        width={width}
        height={height}
        rx={3}
        fill="#ec4899"
      />
      <text
        x={x}
        y={y + 4}
        textAnchor="middle"
        fontSize={10}
        fontFamily="ui-sans-serif, system-ui"
        fill="white"
        fontWeight={500}
      >
        {text}
      </text>
    </g>
  );
}
